import React, { useState, useEffect, useCallback } from 'react';
import supabase from './supabaseClient';
import ProfilePictureUpload from './ProfilePictureUpload';
import SocialPrivacySettings from './SocialPrivacySettings';

interface ClientPrivacyModalProps {
  isOpen: boolean;
  onClose: () => void;
  telegramUserId: string;
  clientName: string;
  onProfileUpdated?: () => void;
  onUpgrade?: () => void;
}

export default function ClientPrivacyModal({ 
  isOpen, 
  onClose, 
  telegramUserId, 
  clientName,
  onProfileUpdated,
  onUpgrade,
}: ClientPrivacyModalProps) {
  const [activeTab, setActiveTab] = useState<'picture' | 'privacy'>('picture');
  const [profilePictureUrl, setProfilePictureUrl] = useState<string>('');
  const [loadingPicture, setLoadingPicture] = useState(false);

  const loadProfilePicture = useCallback(async () => {
    try {
      setLoadingPicture(true);
      const { data, error } = await supabase
        .from('client_profiles')
        .select('profile_picture_url')
        .eq('telegram_user_id', telegramUserId)
        .single();

      if (!error && data) {
        setProfilePictureUrl(data.profile_picture_url || '');
      }
    } catch (error) {
      console.error('Error loading profile picture:', error);
    } finally {
      setLoadingPicture(false);
    }
  }, [telegramUserId]);

  useEffect(() => {
    if (isOpen && telegramUserId) {
      loadProfilePicture();
    }
  }, [isOpen, telegramUserId, loadProfilePicture]);

  const handlePictureUpdated = (url: string) => {
    setProfilePictureUrl(url);
  };

  const handleSettingsUpdated = () => {
    console.log('Privacy settings updated');
    if (onProfileUpdated) onProfileUpdated();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-[#f8f4e6] rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#f8f4e6] border-b border-[#d4d4d4] p-4 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-[#1f1f1f]">
              🔧 {clientName} - Privacy & Profile Settings
            </h2>
            <button
              onClick={onClose}
              className="text-[#666] hover:text-[#1f1f1f] text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-[#d4d4d4]">
          <div className="flex">
            <button
              onClick={() => setActiveTab('picture')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'picture'
                  ? 'bg-[#0eeaff] text-[#1f1f1f] border-b-2 border-[#0eeaff]'
                  : 'bg-white text-[#2d2d2d] hover:bg-[#f8f4e6]'
              }`}
            >
              📸 Profile Picture
            </button>
            <button
              onClick={() => setActiveTab('privacy')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'privacy'
                  ? 'bg-[#0eeaff] text-[#1f1f1f] border-b-2 border-[#0eeaff]'
                  : 'bg-white text-[#2d2d2d] hover:bg-[#f8f4e6]'
              }`}
            >
              🔒 Privacy Settings
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'picture' && (
            <div className="space-y-6">
              <div className="bg-[#8aff8a]/10 border border-[#8aff8a] rounded-lg p-4">
                <h3 className="font-semibold text-[#1f1f1f] mb-2">📸 Profile Picture</h3>
                <p className="text-sm text-[#2d2d2d]">
                  Upload a profile picture for {clientName}. This will be used in the dashboard and social features.
                </p>
              </div>
              
              <ProfilePictureUpload
                telegramUserId={telegramUserId}
                clientName={clientName}
                currentPictureUrl={profilePictureUrl}
                onPictureUpdated={handlePictureUpdated}
                isLoading={loadingPicture}
                onUpgrade={onUpgrade}
              />
            </div>
          )}

          {activeTab === 'privacy' && (
            <div className="space-y-6">
              <div className="bg-[#ffd700]/10 border border-[#ffd700] rounded-lg p-4">
                <h3 className="font-semibold text-[#1f1f1f] mb-2">🔒 Social Privacy Settings</h3>
                <p className="text-sm text-[#2d2d2d]">
                  Control how {clientName} appears in social comparisons and leaderboards.
                </p>
              </div>
              
              <SocialPrivacySettings
                telegramUserId={telegramUserId}
                clientName={clientName}
                onSettingsUpdated={handleSettingsUpdated}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-[#f8f4e6] border-t border-[#d4d4d4] p-4 rounded-b-lg">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-[#d4d4d4] text-[#2d2d2d] rounded hover:bg-[#c4c4c4] transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 